/**
 * PATH: packages/email-hub/src/feature/components/email-allocation/WatchersControl.tsx
 * FILE_ID: EMAILHUB-ALLOCATION-WATCHER-001-VIEW
 * PURPOSE: Renders watcher avatars, removal affordances, and the invite picker.
 */
import { useRef } from 'react';
import { Users, X } from 'lucide-react';
import { AdaptiveOverlay } from '@quotech/ui';

import { cn } from '../../../_internal/lib/utils';
import { AllocationAvatar } from './AllocationAvatar';
import type { WatchersViewModel } from './types';

interface WatchersControlProps {
  vm: WatchersViewModel;
  inline?: boolean;
  /** Hosts that already render a WATCHERS cell label suppress the inner one. */
  showLabel?: boolean;
}

export function WatchersControl({ vm, inline = false, showLabel = true }: WatchersControlProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <div
      className={cn(
        'relative flex min-w-0 items-center gap-2',
        inline ? 'w-full flex-wrap' : 'basis-full sm:ml-auto sm:basis-auto'
      )}
    >
      {showLabel && (
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Watchers</span>
      )}
      <div
        className="flex min-w-0 max-w-full flex-wrap items-center gap-0.5"
        data-testid="email-allocation-watchers"
      >
        {vm.watcherList.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">none</span>
        )}
        {vm.watcherList.map((watcher) => (
          <button
            key={watcher.user_id}
            type="button"
            onClick={() => void vm.removeWatcher(watcher)}
            disabled={!!vm.busyKey}
            className="group relative min-h-11 min-w-11 touch-manipulation focus:z-10 sm:min-h-0 sm:min-w-0"
            title={`${watcher.user_name || watcher.user_email} - click to remove`}
            data-testid={`email-allocation-watcher-${watcher.user_id}`}
          >
            <AllocationAvatar
              size="sm"
              name={watcher.user_name}
              email={watcher.user_email}
              avatar_url={vm.members.find((member) => member.id === watcher.user_id)?.avatar_url}
            />
            <span
              className={cn(
                'absolute -top-1 -right-1 hidden group-hover:flex',
                'h-3 w-3 rounded-full bg-rose-600 text-white items-center justify-center'
              )}
            >
              <X className="h-2 w-2" />
            </span>
          </button>
        ))}
      </div>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => vm.setShowPicker((showing) => !showing)}
        disabled={!!vm.busyKey || vm.inviteCandidates.length === 0}
        className={cn(
          'inline-flex min-h-11 touch-manipulation items-center gap-1 rounded-full border border-dashed px-2 py-0.5 sm:min-h-0',
          'text-[11px] text-muted-foreground hover:bg-muted/60 transition',
          (vm.busyKey || vm.inviteCandidates.length === 0) && 'opacity-50'
        )}
        data-testid="email-allocation-invite-button"
        title="Invite teammate to follow this thread"
      >
        <Users className="h-3 w-3" />
        <span>Invite</span>
      </button>
      <AdaptiveOverlay
        open={vm.showPicker}
        onOpenChange={vm.setShowPicker}
        anchorRef={triggerRef}
        label="Invite email watcher"
        mode="auto"
        popoverWidth={256}
        testId="email-allocation-invite-popover"
      >
        <div
          className="max-h-64 overflow-y-auto p-1"
          role="listbox"
          aria-label="Teammates available to watch"
        >
          {vm.inviteCandidates.length === 0 && (
            <p className="text-[11px] text-muted-foreground p-2">Everyone is already watching.</p>
          )}
          {vm.inviteCandidates.map((member) => (
            <button
              key={member.id}
              type="button"
              role="option"
              onClick={() => void vm.inviteWatcher(member)}
              className={cn(
                'w-full text-left px-2 py-1.5 rounded text-[12px]',
                'flex items-center gap-2 hover:bg-muted'
              )}
            >
              <AllocationAvatar
                size="xs"
                name={member.name}
                email={member.email}
                avatar_url={member.avatar_url}
              />
              <span className="truncate">{member.name}</span>
            </button>
          ))}
        </div>
      </AdaptiveOverlay>
    </div>
  );
}
